import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { useNavigate } from "react-router-dom";
import {
  User,
  Globe,
  FileText,
  Eye,
  CheckCircle,
  XCircle,
  Clock,
  AlertCircle,
  Users,
  FileUser,
  Download,
} from "lucide-react";
import { Card, CardContent } from "../../../ui/card";
import { Button } from "../../../ui/button";
import { Input } from "../../../ui/input";
import { InputFieldset } from "ui/inputFieldset";
import { Label } from "../../../ui/label";
import { Badge } from "../../../ui/badge";
import { Switch } from "../../../ui/switch";
// import { useState } from 'react';
import { toast } from "sonner";
import { FullScreenLoading } from "../../../components/ui/loader";
import {
  getMiqaatRotationDetails,
  getVisaApplicationById,
} from "../../../services/visaService";
import { getFileViewUrl } from "../../../utils/commonutils";
import RotationDetailsCard from "./miqaatRotationDetailCard";
import { usePageHeader } from "contexts/PageHeaderContext";

interface VisaApplicantViewPageProps {
  onNavigate?: (page: string) => void;
}

type DocumentStatus = "approved" | "rejected" | "pending" | "request-more-info";

interface VisaDocument {
  documentType: string;
  fileName?: string;
  filePath?: string;
  uploadedDate?: string;
  status: DocumentStatus;
  rejectionRemark?: string;
}

interface VisaApplicant {
  // Core
  visaApplicationId?: number;
  applicationNumber?: string;
  itsNo?: string;
  referenceId?: string;
  currentStatusCode?: string;
  createdOn?: string;
  visaFee: string;

  // Applicant
  loginId?: string;
  applicantType?: string;
  category?: string;
  guardianITS?: string;
  hofITS?: string;
  title?: string;
  firstName?: string;
  middleName?: string;
  lastName?: string;
  dateOfBirth?: string;
  age?: number;
  gender?: string;
  birthPlace?: string;
  nationality?: string;
  profession?: string;

  // Visa flags
  visaRequired?: boolean;
  visaNotRequiredRemark?: string;
  isMiqaatApplicant?: boolean;
  visaCost?: number;

  // Passport
  passportNo?: string;
  passportType?: string;
  passportFirstName?: string;
  passportMiddleName?: string;
  passportLastName?: string;
  passportArabicName?: string;
  passportDOB?: string;
  passportGender?: string;
  passportNationality?: string;
  passportIssueDate?: string;
  passportExpiryDate?: string;
  passportIssuePlace?: string;

  // Family
  fatherName?: string;
  fatherNameArabic?: string;
  grandfatherName?: string;
  grandfatherNameArabic?: string;
  motherName?: string;
  motherNameArabic?: string;
  mothersFatherName?: string;
  mothersFatherNameArabic?: string;

  // Documents
  documents?: VisaDocument[];

  // Remarks / rejection
  remarks?: string;

  // E-Visa
  eVisaDetails?: {
    nameOnVisa?: string;
    visaNumber?: string;
    visaType?: string;
    issueDate?: string;
    expiryDate?: string;
    entryType?: string;
    duration?: string;
    sponsorName?: string;
    nationality?: string;
    passportNumber?: string;
  };
}

export default function VisaApplicantViewPage({
  onNavigate,
}: VisaApplicantViewPageProps) {
  const { id } = useParams(); // <-- application id from URL
  const navigate = useNavigate();
  const { setPageHeader } = usePageHeader();
  const [applicantData, setApplicantData] = useState<VisaApplicant | null>(
    null,
  );
  const [loading, setLoading] = useState(true);
  const [rotationDetails, setRotationDetails] = useState(null);

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const mapDocumentsForView = (documents: any[] = []) => {
    return documents.flatMap((doc) => {
      if (!doc.versions || doc.versions.length === 0) return [];

      // const latestVersion = doc.versions[doc.versions.length - 1];
      const latestVersion = doc.versions[0];
      const latestHistory = latestVersion.histories?.[0];

      return {
        documentType: doc.documentName,
        fileName: latestVersion.fileName,
        filePath: latestVersion.filePath,
        uploadedDate: latestHistory?.actionAt,
        status: latestVersion.documentVersionStatus.toLowerCase(), // approved | rejected
      };
    });
  };

  useEffect(() => {
    setPageHeader({
      title: "View Applicant Details",
      icon: <FileUser className="h-4 w-4" />,
      onBack: () => navigate("/visa-applicant-management"),
    });

    return () => setPageHeader(null);
  }, [navigate, setPageHeader]);

  useEffect(() => {
    if (!id) return;

    const fetchData = async () => {
      try {
        setLoading(true);

        // 1 FIRST API → GET VISA APPLICATION DETAILS BY ID
        const data = await getVisaApplicationById(Number(id));
        setApplicantData({
          ...data,
          documents: mapDocumentsForView(data.documents),
        });

        // 2 SECOND API → ROTATION DETAILS (FOR MIQAAT TOURS)
        const rotationId = data?.rotationId;
        const tourOperatorId = data?.tourOperatorId;
        if (!rotationId || rotationId === null || !tourOperatorId) return;
        const miqaatRotationDetails = await getMiqaatRotationDetails(
          String(tourOperatorId),
          rotationId,
        );
        setRotationDetails(miqaatRotationDetails);

        // 🔐 Safety redirect
        if (data.currentStatusCode === "REQUEST_MORE_INFO") {
          onNavigate?.(`/visa-applicant-more-info-view/${id}`);
        }
      } catch (error) {
        toast.error("Failed to load applicant details");
      } finally {
        setLoading(false);
      }
    };

    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const handleViewDocument = (file?: string) => {
    if (!file) {
      alert("No document available");
      return;
    }
    // alert(`Viewing ${file}`);
    const fullUrl = getFileViewUrl(file);
    window.open(fullUrl, "_blank");
  };

  if (loading) {
    return (
      <div>
        <FullScreenLoading />
      </div>
    );
  }

  if (!applicantData) {
    return <div className="p-6 text-sm text-red-600">Applicant not found</div>;
  }

  const approvedCount =
    applicantData.documents?.filter(
      (d: VisaDocument) => d.status === "approved",
    ).length ?? 0;

  const totalCount = applicantData.documents?.length ?? 0;

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-white p-3 md:p-6">
      <div className="max-w-6xl mx-auto space-y-4 md:space-y-5">
        {/* MIQAAT ROTATION DETAILS CARD SHOW  */}
        {rotationDetails && (
          <RotationDetailsCard rotationDetails={rotationDetails} />
        )}
        {/* MIQAAT ROTATION DETAILS END  */}

        {/* Top Info Bar */}
        <div className="bg-white rounded-xl border border-gray-200 p-3 md:p-4 shadow-sm">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
            <div className="flex flex-col sm:flex-row sm:items-center gap-3 sm:gap-6">
              <div>
                <p className="text-xs text-gray-500">Application No.</p>
                <p
                  className="text-sm font-semibold"
                  style={{ color: "#5B9BD5" }}
                >
                  {applicantData.applicationNumber}
                </p>
              </div>
              <div className="hidden sm:block h-8 w-px bg-gray-200" />
              <div>
                <p className="text-xs text-gray-500">Reference Code</p>
                <p
                  className="text-sm text-gray-900 font-semibold"
                >
                  {applicantData?.referenceId ?? "-"}
                </p>
              </div>
              <div className="hidden sm:block h-8 w-px bg-gray-200" />
              <div>
                <p className="text-xs text-gray-500">ITS Number</p>
                <p className="text-sm font-semibold text-gray-900">
                  {applicantData.itsNo}
                </p>
              </div>
              <div className="hidden sm:block h-8 w-px bg-gray-200" />
              <div>
                <p className="text-xs text-gray-500">Applied Date</p>
                <p className="text-sm font-medium text-gray-900">
                  {applicantData.createdOn?.split("T")[0]}
                </p>
              </div>
              <div className="hidden sm:block h-8 w-px bg-gray-200" />
              <div>
                <p className="text-xs text-gray-500">Status</p>
                <Badge
                  className="px-2 py-0.5 text-xs mt-1"
                  style={{ backgroundColor: "#5B9BD5", color: "white" }}
                >
                  {applicantData.currentStatusCode}
                </Badge>
              </div>
            </div>
            <div className="flex items-center gap-4">
              <div>
                <p className="text-xs text-gray-500 md:text-right">Visa Cost</p>
                <p className="text-sm font-semibold text-gray-900">
                  ${applicantData.visaFee}
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Personal & Basic Details */}
        <Card
          className="bg-white shadow-sm border border-gray-200 rounded-xl"
          style={{ gap: "0px" }}
        >
          <div className="bg-gray-50 border-b border-gray-200 px-4 py-3 flex items-center gap-2">
            <User className="w-4 h-4" style={{ color: "#5B9BD5" }} />
            <h3 className="text-sm font-semibold" style={{ color: "#5B9BD5" }}>
              Personal & Basic Details
            </h3>
          </div>
          <CardContent className="p-4">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              <div className="space-y-2">
                <InputFieldset
                  label="ITS Number"
                  value={applicantData.itsNo}
                  disabled
                  required
                  className="border-gray-500 rounded-lg text-sm"
                />
              </div>
              <div className="space-y-2">
                <InputFieldset
                  label="Title"
                  value={applicantData.title}
                  disabled
                  placeholder="Title"
                  className="border-gray-500 rounded-lg text-sm"
                />
              </div>
              <div className="space-y-2">
                <InputFieldset
                  label="First Name"
                  value={applicantData.firstName}
                  disabled
                  required
                  className="border-gray-500 rounded-lg text-sm"
                />
              </div>
              <div className="space-y-2">
                <InputFieldset
                  label="Middle Name"
                  value={applicantData.middleName}
                  disabled
                  className="border-gray-500 rounded-lg text-sm"
                />
              </div>
            </div>
            <div
              className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4"
              style={{ marginTop: "16px" }}
            >
              <div className="space-y-2">
                <InputFieldset
                  label="Last Name"
                  required
                  value={applicantData.lastName}
                  disabled
                  className="border-gray-500 rounded-lg text-sm"
                />
              </div>
              <div className="space-y-2">
                <InputFieldset
                  label="Date of Birth"
                  type="date"
                  value={applicantData.dateOfBirth?.split("T")[0]}
                  disabled
                  required
                  className="border-gray-500 rounded-lg text-sm"
                />
              </div>
              <div className="space-y-2">
                <InputFieldset
                  label="Age"
                  value={applicantData.age}
                  disabled
                  className="border-gray-500 rounded-lg text-sm"
                />
              </div>
              <div className="space-y-2">
                <InputFieldset
                  label="Gender"
                  value={applicantData.gender}
                  disabled
                  required
                  className="border-gray-500 rounded-lg text-sm"
                />
              </div>
            </div>
            <div
              className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4"
              style={{ marginTop: "16px" }}
            >
              <div className="space-y-2">
                <InputFieldset
                  label="Nationality"
                  value={applicantData.nationality}
                  disabled
                  required
                  className="border-gray-500 rounded-lg text-sm"
                />
              </div>
              <div className="space-y-2">
                <InputFieldset
                  label="Applicant Type"
                  required
                  value={
                    applicantData.applicantType
                      ? applicantData.applicantType.charAt(0).toUpperCase() +
                        applicantData.applicantType.slice(1)
                      : ""
                  }
                  disabled
                  className="border-gray-500 rounded-lg text-sm"
                />
              </div>
              <div className="hidden space-y-2">
                <InputFieldset
                  label="Category"
                  value={applicantData.category}
                  disabled
                  placeholder="Category"
                  className="border-gray-500 rounded-lg text-sm "
                />
              </div>

              <div className="space-y-2">
                <InputFieldset
                  label="Guardian ITS"
                  value={applicantData.guardianITS}
                  disabled
                  placeholder="Enter Guardian ITS"
                  className="border-gray-500 rounded-lg"
                />
              </div>

              <div className="space-y-2">
                <InputFieldset
                  label="HOF ITS"
                  value={applicantData.hofITS}
                  disabled
                  placeholder="Enter HOF ITS"
                  className="border-gray-500 rounded-lg"
                />
              </div>
            </div>
            <div
              className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4"
              style={{ marginTop: "16px", alignItems: "center" }}
            >
              <div>
                <div className="space-y-2">
                  <div className="flex items-center gap-2 mt-1">
                    <Label className="text-sm text-gray-700">
                      Visa Required
                    </Label>
                    <Switch
                      checked={applicantData.visaRequired}
                      disabled
                      className="scale-75"
                    />
                  </div>
                </div>
                {!applicantData.visaRequired &&
                  applicantData.visaNotRequiredRemark && (
                    <div className="md:col-span-3 lg:col-span-3">
                      <Label className="text-sm text-gray-700">
                        Visa Not Required Remark
                      </Label>
                      <Input
                        value={applicantData.visaNotRequiredRemark}
                        disabled
                        className="border-gray-500 rounded-lg text-sm"
                      />
                    </div>
                  )}
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Passport Details */}
        <Card
          className="bg-white shadow-sm border border-gray-200 rounded-xl"
          style={{ gap: "0px" }}
        >
          <div className="bg-gray-50 border-b border-gray-200 px-4 py-3 flex items-center gap-2">
            <Globe className="w-4 h-4" style={{ color: "#5B9BD5" }} />
            <h3 className="text-sm font-semibold" style={{ color: "#5B9BD5" }}>
              Passport Details
            </h3>
          </div>
          <CardContent className="p-4">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              <div className="space-y-2">
                <InputFieldset
                  label="Passport Number"
                  required
                  value={applicantData.passportNo}
                  disabled
                  placeholder="Passport Number"
                  className="border-gray-500 rounded-lg text-sm"
                />
              </div>
              <div className="space-y-2">
                <InputFieldset
                  label="Passport Type"
                  required
                  value={applicantData.passportType}
                  disabled
                  className="border-gray-500 rounded-lg text-sm"
                />
              </div>
              <div className="space-y-2">
                <InputFieldset
                  label="Place of Issue"
                  value={applicantData.passportIssuePlace}
                  disabled
                  placeholder="Issue place"
                  className="border-gray-500 rounded-lg text-sm"
                />
              </div>
              <div className="space-y-2">
                <InputFieldset
                  label="Profession"
                  value={applicantData.profession}
                  disabled
                  placeholder="Profession"
                  className="border-gray-500 rounded-lg text-sm"
                />
              </div>
            </div>
            <div
              className="grid grid-cols-1 md:grid-cols-3 gap-4"
              style={{ marginTop: "16px" }}
            >
              <div className="space-y-2">
                <InputFieldset
                  label="Passport First Name"
                  required
                  value={applicantData.passportFirstName}
                  disabled
                  placeholder="As per passport"
                  className="border-gray-500 rounded-lg text-sm"
                />
              </div>
              <div className="space-y-2">
                <InputFieldset
                  label="Passport Middle Name"
                  value={applicantData.passportMiddleName}
                  disabled
                  placeholder="As per passport"
                  className="border-gray-500 rounded-lg text-sm"
                />
              </div>
              <div className="space-y-2">
                <InputFieldset
                  label="Passport Last Name"
                  required
                  value={applicantData.passportLastName}
                  disabled
                  placeholder="As per passport"
                  className="border-gray-500 rounded-lg text-sm"
                />
              </div>
            </div>
            <div
              className="grid grid-cols-1 md:grid-cols-3 gap-4"
              style={{ marginTop: "16px" }}
            >
              <div className="space-y-2">
                <InputFieldset
                  label=" Passport Arabic Name"
                  value={applicantData.passportArabicName}
                  disabled
                  placeholder="Arabic name"
                  className="border-gray-500 rounded-lg text-sm"
                  dir="rtl"
                />
              </div>
              <div className="space-y-2">
                <InputFieldset
                  label="Issue Date"
                  type="date"
                  value={applicantData.passportIssueDate?.split("T")[0]}
                  disabled
                  placeholder="As per passport"
                  className="border-gray-500 rounded-lg text-sm"
                />
              </div>
              <div className="space-y-2">
                <InputFieldset
                  label="Expiry Date"
                  type="date"
                  value={applicantData.passportExpiryDate?.split("T")[0]}
                  disabled
                  placeholder="As per passport"
                  className="border-gray-500 rounded-lg text-sm"
                />
              </div>
            </div>
            <div
              className="grid grid-cols-1 md:grid-cols-3 gap-4"
              style={{ marginTop: "16px" }}
            >
              <div className="space-y-2">
                <InputFieldset
                  label="Passport Date of Birth"
                  required
                  type="date"
                  value={applicantData.passportDOB?.split("T")[0]}
                  disabled
                  placeholder="As per passport"
                  className="border-gray-500 rounded-lg text-sm"
                />
              </div>
              <div className="space-y-2">
                <InputFieldset
                  label="Passport Gender"
                  value={applicantData.passportGender}
                  disabled
                  placeholder="As per passport"
                  className="border-gray-500 rounded-lg text-sm"
                />
              </div>
              <div className="space-y-2">
                <InputFieldset
                  label="Passport Nationality"
                  value={applicantData.passportNationality}
                  disabled
                  placeholder="As per passport"
                  className="border-gray-500 rounded-lg text-sm"
                />
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Family Details */}
        <Card
          className="bg-white shadow-sm border border-gray-200 rounded-xl"
          style={{ gap: "0px" }}
        >
          <div className="bg-gray-50 border-b border-gray-200 px-4 py-3 flex items-center">
            <Users className="w-4 h-4" style={{ color: "#5B9BD5" }} />
            <h3 className="text-sm font-semibold" style={{ color: "#5B9BD5" }}>
              Family Details
            </h3>
          </div>
          <CardContent className="p-4">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
              <div className="space-y-2">
                <InputFieldset
                  label="Father's Name"
                  value={applicantData.fatherName}
                  disabled
                  placeholder="Father's name"
                  className="border-gray-500 rounded-lg text-sm"
                />
              </div>
              <div className="space-y-2">
                <InputFieldset
                  label=" Father's Name (Arabic)"
                  value={applicantData.fatherNameArabic}
                  disabled
                  placeholder="Father's name in Arabic"
                  className="border-gray-500 rounded-lg text-sm"
                  dir="rtl"
                />
              </div>
              <div className="space-y-2">
                <InputFieldset
                  label="Grandfather's Name"
                  value={applicantData.grandfatherName}
                  disabled
                  placeholder="Grandfather's name"
                  className="border-gray-500 rounded-lg text-sm"
                />
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3 mt-3">
              <div className="space-y-2">
                <InputFieldset
                  label=" Grandfather's Name (Arabic)"
                  value={applicantData.grandfatherNameArabic}
                  disabled
                  placeholder="Grandfather's name in Arabic"
                  className="border-gray-500 rounded-lg text-sm"
                  dir="rtl"
                />
              </div>
              <div className="space-y-2">
                <InputFieldset
                  label="Mother's Name"
                  value={applicantData.motherName}
                  disabled
                  placeholder="Mother's name"
                  className="border-gray-500 rounded-lg text-sm"
                />
              </div>
              <div className="space-y-2">
                <InputFieldset
                  label=" Mother's Name (Arabic)"
                  value={applicantData.motherNameArabic}
                  disabled
                  placeholder="Mother's name in Arabic"
                  className="border-gray-500 rounded-lg text-sm"
                  dir="rtl"
                />
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3 mt-3">
              <div className="space-y-2">
                <InputFieldset
                  label="Mother's Father Name"
                  value={applicantData.mothersFatherName}
                  disabled
                  placeholder="Mother's father name"
                  className="border-gray-500 rounded-lg text-sm"
                />
              </div>
              <div className="space-y-2">
                <InputFieldset
                  label="Mother's Father Name (Arabic)"
                  value={applicantData.mothersFatherNameArabic}
                  disabled
                  placeholder="Mother's father name in Arabic"
                  className="border-gray-500 rounded-lg text-sm"
                  dir="rtl"
                />
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Documents */}
        <Card className="bg-white shadow-sm border border-gray-200 rounded-xl">
          <div className="bg-gray-50 border-b border-gray-200 px-4 py-3 flex items-center justify-between">
            <div className="flex items-center gap-2">
              <FileText className="w-4 h-4" style={{ color: "#5B9BD5" }} />
              <h3
                className="text-sm font-semibold"
                style={{ color: "#5B9BD5" }}
              >
                Uploaded Documents
              </h3>
            </div>

            <Badge className="bg-gray-100 text-gray-700 text-xs px-2 py-0.5">
              {approvedCount}/{totalCount} Approved
            </Badge>
          </div>

          <CardContent className="p-4">
            <div className="space-y-2">
              {applicantData.documents?.map(
                (doc: VisaDocument, index: number) => (
                  <div
                    key={index}
                    className="border border-gray-200 rounded-lg p-3 bg-gray-50/50 hover:bg-gray-50 transition-colors"
                  >
                    <div className="flex items-center gap-3">
                      {/* Index */}
                      <div className="flex-shrink-0 w-6 h-6 rounded-full bg-white border border-gray-300 flex items-center justify-center">
                        <span className="text-sm text-gray-700">
                          {index + 1}
                        </span>
                      </div>

                      {/* Document Name */}
                      <div className="flex-1 min-w-0">
                        <p className="text-xs font-medium text-gray-900">
                          {doc.documentType}
                        </p>
                        {doc.fileName && (
                          <p className="text-xs text-gray-500 mt-0.5">
                            {doc.fileName && doc.uploadedDate && (
                              <p className="text-xs text-gray-500 mt-0.5">
                                {doc.fileName} •{" "}
                                {new Date(doc.uploadedDate).toLocaleDateString(
                                  "en-US",
                                  {
                                    month: "short",
                                    day: "numeric",
                                    year: "numeric",
                                  },
                                )}
                              </p>
                            )}
                          </p>
                        )}
                      </div>

                      {/* Status Badge */}
                      <div className="flex-shrink-0">
                        {doc.status === "approved" && (
                          <Badge className="bg-green-100 text-green-700 border-green-200 text-xs px-2 py-0.5">
                            <CheckCircle className="w-3 h-3 mr-1" />
                            Approved
                          </Badge>
                        )}
                        {doc.status === "rejected" && (
                          <Badge className="bg-red-100 text-red-700 border-red-200 text-xs px-2 py-0.5">
                            <XCircle className="w-3 h-3 mr-1" />
                            Rejected
                          </Badge>
                        )}
                        {doc.status === "request-more-info" && (
                          <Badge className="bg-amber-100 text-amber-700 border-amber-200 text-xs px-2 py-0.5">
                            <AlertCircle className="w-3 h-3 mr-1" />
                            More Info
                          </Badge>
                        )}
                        {doc.status === "pending" && (
                          <Badge className="bg-gray-100 text-gray-600 border-gray-200 text-xs px-2 py-0.5">
                            <Clock className="w-3 h-3 mr-1" />
                            Pending
                          </Badge>
                        )}
                      </div>

                      {/* Actions */}
                      <div className="flex items-center gap-2">
                        {doc.fileName && (
                          <Button
                            size="sm"
                            variant="outline"
                            className="h-7 px-3 text-xs rounded-md"
                            onClick={() => handleViewDocument(doc.filePath)}
                          >
                            <Eye className="w-3 h-3 mr-1" />
                            View
                          </Button>
                        )}
                      </div>
                    </div>
                  </div>
                ),
              )}
            </div>

            {/* <div className="mt-3 p-2 bg-blue-50 border border-blue-200 rounded-md">
              <p className="text-xs text-blue-700">
                📎 JPEG format only • Maximum 5MB per file
              </p>
            </div> */}
          </CardContent>
        </Card>

        {/* E-Visa Certificate */}
        {applicantData.currentStatusCode === "APPROVED" &&
          applicantData.visaRequired &&
          applicantData.eVisaDetails && (
            <Card className="bg-white shadow-sm border border-green-200 rounded-xl">
              <div className="bg-green-50 border-b border-green-200 px-4 py-3 flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <CheckCircle className="w-4 h-4 text-green-600" />
                  <h3 className="text-sm font-semibold text-green-700">
                    E-Visa Certificate
                  </h3>
                  <Badge className="bg-green-100 text-green-700 border-green-300 text-xs px-2 py-0.5">
                    Approved
                  </Badge>
                </div>
                <Button
                  size="sm"
                  className="h-8 px-3 text-xs bg-[#5B9BD5] hover:bg-[#4A8AC5] text-white"
                  onClick={() =>
                    toast.success("Downloading E-Visa Certificate...")
                  }
                >
                  <Download className="w-3 h-3 mr-1" />
                  Download Certificate
                </Button>
              </div>
              <CardContent className="p-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <div className="md:col-span-1">
                    <Label className="text-sm text-gray-700">
                      Name on Visa
                    </Label>
                    <Input
                      value={applicantData.eVisaDetails.nameOnVisa}
                      disabled
                      className="border-gray-500 rounded-lg text-sm font-semibold"
                    />
                  </div>
                  <div className="md:col-span-1">
                    <Label className="text-sm text-gray-700">Visa Number</Label>
                    <Input
                      value={applicantData.eVisaDetails.visaNumber}
                      disabled
                      className="border-gray-500 rounded-lg text-sm font-mono font-semibold"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mt-3">
                  <div className="md:col-span-1">
                    <Label className="text-sm text-gray-700">Visa Type</Label>
                    <Input
                      value={applicantData.eVisaDetails.visaType}
                      disabled
                      className="border-gray-500 rounded-lg text-sm"
                    />
                  </div>
                  <div className="md:col-span-1">
                    <Label className="text-sm text-gray-700">Entry Type</Label>
                    <Input
                      value={applicantData.eVisaDetails.entryType}
                      disabled
                      className="border-gray-500 rounded-lg text-sm"
                    />
                  </div>
                  <div className="md:col-span-1">
                    <Label className="text-sm text-gray-700">Duration</Label>
                    <Input
                      value={applicantData.eVisaDetails.duration}
                      disabled
                      className="border-gray-500 rounded-lg text-sm"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mt-3">
                  <div className="md:col-span-1">
                    <Label className="text-sm text-gray-700">Issue Date</Label>
                    <Input
                      type="date"
                      value={applicantData.eVisaDetails.issueDate}
                      disabled
                      className="border-gray-500 rounded-lg text-sm"
                    />
                  </div>
                  <div className="md:col-span-1">
                    <Label className="text-sm text-gray-700">Expiry Date</Label>
                    <Input
                      type="date"
                      value={applicantData.eVisaDetails.expiryDate}
                      disabled
                      className="border-gray-500 rounded-lg text-sm"
                    />
                  </div>
                  <div className="md:col-span-1">
                    <Label className="text-sm text-gray-700">Nationality</Label>
                    <Input
                      value={applicantData.eVisaDetails.nationality}
                      disabled
                      className="border-gray-500 rounded-lg text-sm"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mt-3">
                  <div className="md:col-span-1">
                    <Label className="text-sm text-gray-700">
                      Passport Number
                    </Label>
                    <Input
                      value={applicantData.eVisaDetails.passportNumber}
                      disabled
                      className="border-gray-500 rounded-lg text-sm"
                    />
                  </div>
                  <div className="md:col-span-1">
                    <Label className="text-sm text-gray-700">
                      Sponsor Name
                    </Label>
                    <Input
                      value={applicantData.eVisaDetails.sponsorName}
                      disabled
                      className="border-gray-500 rounded-lg text-sm"
                    />
                  </div>
                </div>

                {/* Disclaimer */}
                <div className="mt-4 p-3 bg-amber-50 border border-amber-200 rounded-lg">
                  <div className="flex items-start gap-2">
                    <AlertCircle className="w-4 h-4 text-amber-600 flex-shrink-0 mt-0.5" />
                    <div>
                      <p className="text-xs font-semibold text-amber-800 mb-1">
                        Important Notice
                      </p>
                      <p className="text-xs text-amber-700">
                        The original e-visa certificate will be shared or
                        becomes available{" "}
                        <strong>3 days before the tour departure date</strong>.
                        Please ensure you download and carry a printed copy
                        along with your passport during travel.
                      </p>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}

        {/* Rejection Card - For Rejected Applications */}
        {applicantData.currentStatusCode === "REJECTED" &&
          applicantData.remarks && (
            <Card className="bg-white shadow-sm border border-red-200 rounded-xl">
              <div className="bg-red-50 border-b border-red-200 px-4 py-3 flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <XCircle className="w-4 h-4 text-red-600" />
                  <h3 className="text-sm font-semibold text-red-700">
                    {applicantData.visaRequired
                      ? "E-Visa Rejection"
                      : "Application Rejection"}
                  </h3>
                  <Badge className="bg-red-100 text-red-700 border-red-300 text-xs px-2 py-0.5">
                    Rejected
                  </Badge>
                </div>
              </div>
              <CardContent className="p-4">
                <div className="space-y-3">
                  {/* Rejection Type */}
                  <div>
                    <Label className="text-sm text-gray-700">
                      Rejection Type
                    </Label>
                    <Input
                      value={
                        applicantData.visaRequired
                          ? "E-Visa Department Rejection"
                          : "Admin Rejection"
                      }
                      disabled
                      className="h-8 text-xs mt-1 bg-red-50 border-red-200 font-semibold text-red-700"
                    />
                  </div>

                  {/* Rejection Reason */}
                  <div>
                    <Label className="text-sm text-gray-700">
                      Rejection Reason
                    </Label>
                    <div className="mt-1 p-3 bg-red-50 border border-red-200 rounded-lg">
                      <p className="text-xs text-red-700">
                        {applicantData.remarks}
                      </p>
                    </div>
                  </div>

                  {/* Info Box based on visa required status */}
                  <div
                    className={`p-3 ${applicantData.visaRequired ? "bg-amber-50 border-amber-200" : "bg-blue-50 border-blue-200"} border rounded-lg`}
                  >
                    <div className="flex items-start gap-2">
                      <AlertCircle
                        className={`w-4 h-4 ${applicantData.visaRequired ? "text-amber-600" : "text-blue-600"} flex-shrink-0 mt-0.5`}
                      />
                      <div>
                        <p
                          className={`text-xs font-semibold ${applicantData.visaRequired ? "text-amber-800" : "text-blue-800"} mb-1`}
                        >
                          {applicantData.visaRequired
                            ? "E-Visa Department Action Required"
                            : "Admin Review Required"}
                        </p>
                        <p
                          className={`text-xs ${applicantData.visaRequired ? "text-amber-700" : "text-blue-700"}`}
                        >
                          {applicantData.visaRequired
                            ? "This application has been rejected by the E-Visa department. Please review the rejection reason above and contact support for further assistance or reapply with corrected information."
                            : "This application has been rejected by the admin. Please review the rejection reason above, correct the documents or information, and resubmit the application."}
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}
      </div>
    </div>
  );
}
